<template>
    <div class="card ap-page">
        <PageHeader
            title="Budget Allocation Approval"
            subtitle="Review budget versions submitted from Detail Budget Allocation"
            :showBtn="false"
        />

        <div class="filter-section">
            <div class="ap-pills" role="tablist">
                <button
                    v-for="pill in statusPills"
                    :key="pill.id"
                    type="button"
                    role="tab"
                    class="ap-pill"
                    :class="{ 'is-active': status === pill.id }"
                    @click="onClickStatus(pill.id)"
                >{{ pill.name }}</button>
            </div>

            <div class="filter-bar">
                <div class="filter-dates">
                    <div class="filter-date-field">
                        <label class="filter-label">Submitted From</label>
                        <input v-model="start" type="date" class="form-control filter-date-input">
                    </div>
                    <div class="filter-date-field">
                        <label class="filter-label">To</label>
                        <input v-model="end" type="date" class="form-control filter-date-input">
                    </div>
                    <button type="button" class="btn btn-primary filter-go-btn" :disabled="loading" @click="onClickGo">Go</button>
                </div>

                <div class="filter-bar-actions">
                    <span class="result-summary">{{ resultSummary }}</span>
                    <FilterPopover
                        ref="filterPopoverRef"
                        title="Filter Approvals"
                        popoverWidth="360px"
                        :activeCount="activeFilterCount"
                        @open="onOpenFilter"
                        @apply="onApplyFilters"
                        @clear="onClearFilters"
                    >
                        <div class="col-12">
                            <label class="filter-label">Master Budget Line</label>
                            <v-select
                                v-model="draftFilters.project_id"
                                placeholder="Any master budget line"
                                :options="budgetLineOptions"
                                label="name"
                                :reduce="option => option.id"
                            />
                        </div>
                        <div class="col-12 mt-1">
                            <label class="filter-label">Search</label>
                            <input v-model="draftFilters.search" type="text" class="form-control" placeholder="Budget name or code">
                        </div>
                    </FilterPopover>
                </div>
            </div>
        </div>

        <div class="table-section">
            <ApprovalTable
                :rows="versions"
                :loading="loading"
                :status="status"
                @review="onReview"
            />
        </div>

        <div class="pagination-section">
            <Pagination :offset="offset" @onPageChange="onPageChange"/>
        </div>
    </div>
</template>

<script setup>
import { computed, inject, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import PageHeader from '@/components/atom/PageHeader.vue'
import FilterPopover from '@/components/atom/FilterPopover.vue'
import Pagination from '@/components/atom/Pagination'
import ApprovalTable from '@/components/molecule/procurement/budget-tracker/ApprovalTable.vue'
import handleBudgetTracker from '@/services/modules/procurement/budgetTracker'
import { getApiErrorMessage, resetPaginationPage } from '@/services/utils/global.js'
import useAutoRefresh from '@/composables/useAutoRefresh'

const $store = useStore()
const $route = useRoute()
const $router = useRouter()
const showError = inject('showError')
const { fetchBudgetApprovals, fetchBudgetLines } = handleBudgetTracker()

const statusPills = [
    { id: 'pending', name: 'Pending' },
    { id: 'approved', name: 'Approved' },
    { id: 'rejected', name: 'Rejected' }
]

const emptyFilters = () => ({ project_id: null, search: '' })

const offset = ref(20)
const loading = ref(false)
const versions = ref([])
const budgetLineOptions = ref([])
const pageMeta = ref({ from: 0, to: 0, total: 0 })
const status = ref('pending')
const start = ref('')
const end = ref('')
const filterPopoverRef = ref(null)
const draftFilters = ref(emptyFilters())
const appliedFilters = ref(emptyFilters())

const companyId = computed(() => $route.params.companyId)

const activeFilterCount = computed(() => {
    let count = 0
    if (appliedFilters.value.project_id) count++
    if (appliedFilters.value.search) count++
    return count
})

const resultSummary = computed(() => {
    if (loading.value) return 'Loading…'
    if (! pageMeta.value.total) return 'No records'
    return `Showing ${pageMeta.value.from}–${pageMeta.value.to} of ${pageMeta.value.total}`
})

const getQuery = (page) => {
    const filters = appliedFilters.value
    let query = '?company_id=' + companyId.value
    query += '&offset=' + offset.value
    query += '&status=' + status.value
    if (start.value && end.value) query += `&start_date=${start.value}&end_date=${end.value}`
    if (page) query += '&page=' + page
    if (filters.project_id) query += '&project_id=' + filters.project_id
    if (filters.search) query += '&search=' + encodeURIComponent(filters.search)
    return query
}

const clearList = () => {
    versions.value = []
    pageMeta.value = { from: 0, to: 0, total: 0 }
    $store.commit('resetPagination')
}

const applyResponse = (res) => {
    if (! res.status) {
        showError(res.message)
        return clearList()
    }
    versions.value = res.data.data
    pageMeta.value = { from: res.data.from, to: res.data.to, total: res.data.total }
    $store.commit('setPaginationData', {
        records: res.data.total,
        perPage: res.data.per_page,
        currentPage: res.data.current_page,
    })
}

let listRequestNo = 0

const getVersions = async (page) => {
    listRequestNo++
    loading.value = true
    try {
        applyResponse(await fetchBudgetApprovals(getQuery(page)))
    } catch (err) {
        clearList()
        showError(getApiErrorMessage(err))
    } finally {
        loading.value = false
    }
}

const refreshSilently = async () => {
    if (loading.value) return
    const startedAt = listRequestNo
    const res = await fetchBudgetApprovals(getQuery($route.query.page))
    if (startedAt !== listRequestNo) return
    if (! res.status) return
    applyResponse(res)
}

useAutoRefresh(refreshSilently, () => status.value === 'pending')

const getBudgetLines = async () => {
    try {
        const res = await fetchBudgetLines('?company_id=' + companyId.value)
        if (! res.status) return
        budgetLineOptions.value = res.data
    } catch (err) {
        showError(getApiErrorMessage(err))
    }
}

const routeQueryWith = (values) => Object.assign({}, $route.query, values)

const onClickStatus = async (id) => {
    status.value = id
    await $router.push({ path: $route.path, query: routeQueryWith({ status: id, page: 1 }) })
    await getVersions(1)
}

const onClickGo = async () => {
    await $router.push({ path: $route.path, query: routeQueryWith({ start: start.value, end: end.value, page: 1 }) })
    await getVersions(1)
}

const onPageChange = async (page) => {
    await $router.push({ path: $route.path, query: routeQueryWith({ page }) })
    await getVersions(page)
}

const onOpenFilter = () => {
    draftFilters.value = Object.assign({}, appliedFilters.value)
}

const onApplyFilters = async () => {
    appliedFilters.value = Object.assign({}, draftFilters.value)
    resetPaginationPage($store, $route, $router)
    await getVersions(1)
}

const onClearFilters = async () => {
    draftFilters.value = emptyFilters()
    appliedFilters.value = emptyFilters()
    if (filterPopoverRef.value) filterPopoverRef.value.close()
    resetPaginationPage($store, $route, $router)
    await getVersions(1)
}

const onReview = (item) => {
    $router.push({
        name: 'budget-tracker-review',
        params: Object.assign({}, $route.params, { budgetId: item.budget_master_id, versionId: item.id }),
        query: Object.assign({}, $route.query, { status: status.value })
    })
}

const initFilters = () => {
    status.value = statusPills.some(pill => pill.id === $route.query.status) ? $route.query.status : 'pending'
    start.value = $route.query.start || ''
    end.value = $route.query.end || ''
}

onMounted(async () => {
    initFilters()
    await Promise.all([getVersions($route.query.page), getBudgetLines()])
})
</script>

<style scoped>
.ap-page {
    min-height: calc(100vh - 158px);
}
.filter-section {
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 18px 20px 0;
}
.ap-pills {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    padding: 4px;
    border-radius: 10px;
    background: #f2f4f7;
    width: fit-content;
    max-width: 100%;
}
.ap-pill {
    height: 36px;
    padding: 0 18px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    font-size: 14px;
    font-weight: 500;
    color: #475467;
    cursor: pointer;
}
.ap-pill.is-active {
    background: #fff;
    color: #7367f0;
    font-weight: 600;
    box-shadow: 0 1px 3px rgba(16, 24, 40, .1);
}
.filter-bar {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
}
.filter-dates {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    flex-wrap: wrap;
}
.filter-date-field {
    display: flex;
    flex-direction: column;
}
.filter-label {
    display: block;
    margin-bottom: 6px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: .04em;
    text-transform: uppercase;
    color: #344054;
}
.filter-date-input {
    width: 180px;
    height: 40px;
    border-radius: 8px;
    border-color: #e4e7ec;
    font-size: 14px;
}
.filter-go-btn {
    height: 40px;
    min-width: 76px;
    border-radius: 8px;
    font-size: 14px;
}
.filter-bar-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-left: auto;
}
.result-summary {
    font-size: 14px;
    color: #667085;
    white-space: nowrap;
}
.table-section {
    padding: 16px 20px 0;
}
.pagination-section {
    padding: 14px 20px 18px;
}
@media (max-width: 576px) {
    .filter-section {
        padding: 14px 16px 0;
    }
    .table-section {
        padding: 14px 16px 0;
    }
    .pagination-section {
        padding: 12px 16px 16px;
    }
    .filter-date-field {
        flex: 1 1 140px;
    }
    .filter-date-input {
        width: 100%;
    }
    .filter-bar-actions {
        width: 100%;
        justify-content: space-between;
        margin-left: 0;
    }
}
</style>
